import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiCopy, FiRefreshCw, FiAlertTriangle, FiCheckCircle, FiShuffle, FiPlay, FiDownload, FiX, FiLoader, FiEye, FiEyeOff, FiSend } = FiIcons;

const WEBHOOK_URL = 'https://cloud.activepieces.com/api/v1/webhooks/YvFMt2Y9WYAjZaFA9WkQP';
const SORA_API_BASE = 'https://api.openai.com/v1/videos';
const SORA_BETA_HEADER = 'video-preview-2024-12-17'; // Update to match the latest OpenAI preview header when required

function App() {
  const [formData, setFormData] = useState({
    dealershipName: '',
    brand: 'Audi',
    vehicleModel: '',
    offerSnippet: '',
    cta: '',
    locationCue: '',
    tone: 'cinematic',
    style: 'realistic',
    cameraMove: 'slow dolly',
    lighting: 'golden hour',
    seasonalTheme: 'None',
    musicVibe: 'modern cinematic',
    voiceoverText: '',
    voiceoverEndSecond: 7,
    contactEmail: '', // New field for contact information
    safetyConsent: false,
    termsAgreement: false
  });

  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [generatedJSON, setGeneratedJSON] = useState('');
  const [voiceoverWordCount, setVoiceoverWordCount] = useState(0);
  const [copied, setCopied] = useState({ prompt: false, json: false });
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [videoStatus, setVideoStatus] = useState('');
  const [webhookStatus, setWebhookStatus] = useState('');
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [isApiKeySet, setIsApiKeySet] = useState(false);
  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [downloadNotice, setDownloadNotice] = useState('');

  const brands = ['Audi', 'Volkswagen', 'Subaru', 'Other'];
  const tones = ['cinematic', 'energetic', 'cozy', 'premium', 'adventurous'];
  const styles = ['realistic', 'cinematic', 'light documentary', 'social UGC'];
  const cameraMoves = ['slow dolly', 'tracking pass-by', '360° orbit', 'roadside pan'];
  const lightingOptions = ['golden hour', 'overcast', 'showroom softbox', 'night city'];
  const seasonalThemes = ['Fall', 'Winter', 'Spring', 'Summer', 'Halloween', 'Holiday', 'None'];
  const musicVibes = ['upbeat pop', 'modern cinematic', 'chill ambient', 'acoustic'];

  const presets = {
    'Fall Drive B-roll': {
      tone: 'cinematic',
      style: 'realistic',
      cameraMove: 'tracking pass-by',
      lighting: 'golden hour',
      seasonalTheme: 'Fall',
      locationCue: 'tree-lined road with fall foliage',
      musicVibe: 'modern cinematic'
    },
    'Showroom Hero Spin': {
      tone: 'premium',
      style: 'cinematic',
      cameraMove: '360° orbit',
      lighting: 'showroom softbox',
      seasonalTheme: 'None',
      locationCue: 'pristine showroom floor',
      musicVibe: 'modern cinematic'
    },
    'AWD Snow Tease': {
      tone: 'adventurous',
      style: 'realistic',
      cameraMove: 'slow dolly',
      lighting: 'overcast',
      seasonalTheme: 'Winter',
      locationCue: 'snowy mountain road',
      musicVibe: 'chill ambient'
    },
    'Service Check-In': {
      tone: 'cozy',
      style: 'light documentary',
      cameraMove: 'roadside pan',
      lighting: 'overcast',
      seasonalTheme: 'None',
      locationCue: 'welcoming service bay',
      musicVibe: 'acoustic'
    },
    'Halloween Friendly': {
      tone: 'cozy',
      style: 'social UGC',
      cameraMove: 'slow dolly',
      lighting: 'golden hour',
      seasonalTheme: 'Halloween',
      locationCue: 'family-friendly neighborhood with pumpkins',
      musicVibe: 'upbeat pop'
    }
  };

  // Initialize API key from secure storage
  useEffect(() => {
    const storedApiKey = localStorage.getItem('sora_api_key');
    if (storedApiKey) {
      setApiKey(storedApiKey);
      setIsApiKeySet(true);
    }
  }, []);

  // Secure API key handler
  const handleApiKeyChange = (value) => {
    setApiKey(value);
    if (value.trim()) {
      localStorage.setItem('sora_api_key', value.trim());
      setIsApiKeySet(true);
    } else {
      localStorage.removeItem('sora_api_key');
      setIsApiKeySet(false);
    }
  };

  useEffect(() => {
    const words = formData.voiceoverText.trim().split(/\s+/).filter(word => word.length > 0);
    setVoiceoverWordCount(words.length);
  }, [formData.voiceoverText]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const applyPreset = (presetName) => {
    const preset = presets[presetName];
    setFormData(prev => ({ ...prev, ...preset }));
  };

  const buildSoraPayload = (promptText) => {
    const metadata = {};

    if (formData.dealershipName) {
      metadata.dealership_name = formData.dealershipName;
    }
    if (formData.contactEmail) {
      metadata.contact_email = formData.contactEmail;
    }
    if (formData.offerSnippet) {
      metadata.offer_snippet = formData.offerSnippet;
    }
    if (formData.cta) {
      metadata.call_to_action = formData.cta;
    }
    if (formData.tone) {
      metadata.tone = formData.tone;
    }
    if (formData.style) {
      metadata.style = formData.style;
    }
    if (formData.cameraMove) {
      metadata.camera_move = formData.cameraMove;
    }
    if (formData.lighting) {
      metadata.lighting = formData.lighting;
    }
    if (formData.seasonalTheme && formData.seasonalTheme !== 'None') {
      metadata.seasonal_theme = formData.seasonalTheme;
    }
    if (formData.musicVibe) {
      metadata.music_vibe = formData.musicVibe;
    }
    if (formData.brand) {
      metadata.brand = formData.brand;
    }
    if (formData.vehicleModel) {
      metadata.vehicle_model = formData.vehicleModel;
    }
    if (formData.voiceoverText) {
      metadata.voiceover_script = formData.voiceoverText;
      metadata.voiceover_end_second = formData.voiceoverEndSecond;
    }

    const sanitizedPrompt = promptText.trim();

    // Removed duration_seconds: 10, aspect_ratio: '16:9' to comply with known API parameter limitations
    const payload = {
      model: 'sora-2',
      prompt: sanitizedPrompt,
    };

    // Add back metadata and audio configuration
    if (Object.keys(metadata).length > 0) {
      payload.metadata = metadata;
    }

    if (formData.voiceoverText) {
      payload.audio = {
        voiceover: {
          script: formData.voiceoverText,
          end_second: formData.voiceoverEndSecond,
        },
      };
    }

    return payload;
  };

  const generatePrompt = () => {
    const { dealershipName, brand, vehicleModel, offerSnippet, cta, locationCue, tone, style, cameraMove, lighting, seasonalTheme, musicVibe, voiceoverText, voiceoverEndSecond } = formData;

    let prompt = `A ${tone} ${style} 10-second video shot showcasing`;
    
    if (vehicleModel) {
      prompt += ` a ${brand} ${vehicleModel}`;
    } else {
      prompt += ` a ${brand} vehicle`;
    }

    if (locationCue) {
      prompt += ` in ${locationCue}`;
    }

    prompt += `. Camera movement: ${cameraMove}. Lighting: ${lighting}`;

    if (seasonalTheme !== 'None') {
      prompt += `. ${seasonalTheme} seasonal atmosphere`;
    }

    prompt += `. Single continuous scene, no cuts, smooth motion`;

    if (offerSnippet) {
      prompt += `. Subtle text overlay: "${offerSnippet}"`;
    }

    if (cta) {
      prompt += ` and "${cta}"`;
    }

    if (voiceoverText) {
      prompt += `. Include non-intrusive ${musicVibe} background music; time voiceover to finish by ${voiceoverEndSecond} seconds`;
    } else {
      prompt += `. Background music: ${musicVibe}`;
    }

    prompt += `. No readable license plates, family-safe visuals, professional automotive presentation`;

    if (dealershipName) {
      prompt += `. Suitable for ${dealershipName} dealership marketing`;
    }

    setGeneratedPrompt(prompt);

    const jsonPayload = buildSoraPayload(prompt);

    setGeneratedJSON(JSON.stringify(jsonPayload, null, 2));
  };

  const generateVariant = () => {
    const alternativeCameraMoves = cameraMoves.filter(move => move !== formData.cameraMove);
    const alternativeLighting = lightingOptions.filter(light => light !== formData.lighting);
    
    const newCameraMove = alternativeCameraMoves[Math.floor(Math.random() * alternativeCameraMoves.length)];
    const newLighting = alternativeLighting[Math.floor(Math.random() * alternativeLighting.length)];
    
    const tempData = { ...formData, cameraMove: newCameraMove, lighting: newLighting };
    
    let prompt = `A ${tempData.tone} ${tempData.style} 10-second video shot showcasing`;
    
    if (tempData.vehicleModel) {
      prompt += ` a ${tempData.brand} ${tempData.vehicleModel}`;
    } else {
      prompt += ` a ${tempData.brand} vehicle`;
    }

    if (tempData.locationCue) {
      prompt += ` in ${tempData.locationCue}`;
    }

    prompt += `. Camera movement: ${newCameraMove}. Lighting: ${newLighting}`;

    if (tempData.seasonalTheme !== 'None') {
      prompt += `. ${tempData.seasonalTheme} seasonal atmosphere`;
    }

    prompt += `. Single continuous scene, no cuts, smooth motion`;

    if (tempData.offerSnippet) {
      prompt += `. Subtle text overlay: "${tempData.offerSnippet}"`;
    }

    if (tempData.cta) {
      prompt += ` and "${tempData.cta}"`;
    }

    if (tempData.voiceoverText) {
      prompt += `. Include non-intrusive ${tempData.musicVibe} background music; time voiceover to finish by ${tempData.voiceoverEndSecond} seconds`;
    } else {
      prompt += `. Background music: ${tempData.musicVibe}`;
    }

    prompt += `. No readable license plates, family-safe visuals, professional automotive presentation`;

    if (tempData.dealershipName) {
      prompt += `. Suitable for ${tempData.dealershipName} dealership marketing`;
    }

    setGeneratedPrompt(prompt);

    const jsonPayload = buildSoraPayload(prompt);

    setGeneratedJSON(JSON.stringify(jsonPayload, null, 2));
  };

  const sendWebhook = async (videoData, requestPayload) => {
    setWebhookStatus('Sending video data to webhook...');
    try {
      const payload = {
        timestamp: new Date().toISOString(),
        dealership_name: formData.dealershipName || 'N/A',
        contact_email: formData.contactEmail,
        video_prompt: generatedPrompt,
        video_url: videoData.url,
        video_id: videoData.id,
        video_payload: requestPayload || null,
        video_response: videoData.response || null,
        style_details: {
          brand: formData.brand,
          model: formData.vehicleModel,
          tone: formData.tone,
          camera_move: formData.cameraMove,
        }
      };

      const response = await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Webhook failed with status: ${response.status}`);
      }

      setWebhookStatus('Video URL successfully sent to marketing workflow!');
      console.log('Webhook success:', await response.json());

    } catch (error) {
      console.error('Webhook Error:', error);
      setWebhookStatus(`Webhook failed: ${error.message}`);
      // Don't alert here, rely on status message
    }
  };

  const extractVideoUrl = (data) => {
    if (!data) return null;
    if (data.video_url) return data.video_url;
    if (data.assets?.video) return data.assets.video;
    if (Array.isArray(data.output)) {
      for (const entry of data.output) {
        if (entry?.download_url) return entry.download_url;
        if (entry?.url) return entry.url;
      }
    }
    if (data.result?.[0]?.video?.url) return data.result[0].video.url;
    if (data.media?.video) return data.media.video;
    return null;
  };

  const generateVideo = async () => {
    if (!generatedPrompt) {
      alert('Please generate a prompt first');
      return;
    }

    if (!isApiKeySet || !apiKey.trim()) {
      alert('Please set your OpenAI API key first');
      return;
    }

    if (!formData.contactEmail) {
      alert('Please enter your contact email for video delivery.');
      return;
    }

    setIsGeneratingVideo(true);
    setVideoStatus('Initializing video generation...');
    setGeneratedVideo(null);
    setWebhookStatus('');
    setDownloadError('');
    setDownloadNotice('');

    let taskId = null;

    try {
      // Step 1: Create the video generation request
      setVideoStatus('Creating video generation request...');

      const payload = buildSoraPayload(generatedPrompt);

      const createResponse = await fetch(SORA_API_BASE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${apiKey.trim()}`,
          'OpenAI-Beta': SORA_BETA_HEADER,
        },
        body: JSON.stringify(payload),
      });

      const createData = await createResponse.json();

      if (!createResponse.ok) {
        const message = createData?.error?.message || `Request failed with status ${createResponse.status}`;
        throw new Error(message);
      }

      taskId = createData?.id || createData?.data?.id;

      if (!taskId) {
        throw new Error('Unable to retrieve video task ID from OpenAI response.');
      }

      // Step 2: Poll for completion
      let attempts = 0;
      const maxAttempts = 60;
      const pollDelayMs = 5000;

      while (attempts < maxAttempts) {
        setVideoStatus(`Processing video... (attempt ${attempts + 1}/${maxAttempts})`);

        const statusResponse = await fetch(`${SORA_API_BASE}/${taskId}`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${apiKey.trim()}`,
            'OpenAI-Beta': SORA_BETA_HEADER,
          },
        });

        const statusData = await statusResponse.json();

        if (!statusResponse.ok) {
          const message = statusData?.error?.message || `Status check failed with ${statusResponse.status}`;
          throw new Error(message);
        }

        const status = statusData?.status || statusData?.state || statusData?.phase;
        const readableStatus = status ? status.replace(/_/g, ' ') : 'processing';
        setVideoStatus(`Processing video (${readableStatus})... (attempt ${attempts + 1}/${maxAttempts})`);

        if (['completed', 'succeeded', 'ready', 'finished'].includes(status)) {
          const videoUrl = extractVideoUrl(statusData);

          if (!videoUrl) {
            throw new Error('Video completed but no download URL was returned.');
          }

          setVideoStatus('Video generation completed! Preparing preview...');

          const finalVideoData = {
            id: taskId,
            url: videoUrl,
            response: statusData,
          };

          setGeneratedVideo(finalVideoData);
          setShowVideoModal(true);

          await sendWebhook(finalVideoData, payload);
          break;
        }

        if (['failed', 'errored', 'cancelled', 'canceled'].includes(status)) {
          const message = statusData?.error?.message || 'Video generation failed.';
          throw new Error(message);
        }

        await new Promise(resolve => setTimeout(resolve, pollDelayMs));
        attempts++;
      }

      if (attempts >= maxAttempts) {
        throw new Error('Video generation timed out while waiting for completion.');
      }

    } catch (error) {
      console.error('Video generation error:', error);
      setVideoStatus(`Error: ${error.message}`);
      alert(`Failed to generate video: ${error.message}`);
    } finally {
      setIsGeneratingVideo(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [type]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [type]: false })), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Utility to trigger local download (for the simulated video)
  const downloadVideo = async (videoUrl, filename) => {
    if (!videoUrl) return;

    try {
      setIsDownloadingVideo(true);
      setDownloadError('');
      setDownloadNotice('');

      const headers = {};
      if (videoUrl.includes('api.openai.com') && apiKey.trim()) {
        headers.Authorization = `Bearer ${apiKey.trim()}`;
        headers['OpenAI-Beta'] = SORA_BETA_HEADER;
      }

      const response = await fetch(videoUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`Download failed with status ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'dealer-video.mp4';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);

      setDownloadNotice('Download started. Check your browser downloads for the MP4 file.');
      setTimeout(() => setDownloadNotice(''), 5000);
    } catch (error) {
      console.error('Download error:', error);
      setDownloadError(error.message);
    } finally {
      setIsDownloadingVideo(false);
    }
  };

  const voiceoverTooLong = voiceoverWordCount > 25;
  const hasConsents = formData.safetyConsent && formData.termsAgreement;
  const canGeneratePrompt = hasConsents && !voiceoverTooLong;
  const canGenerateVideo =
    hasConsents &&
    Boolean(generatedPrompt) &&
    !isGeneratingVideo &&
    isApiKeySet &&
    !voiceoverTooLong;
  const missingContactEmail = !formData.contactEmail?.trim();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-slate-800 mb-2">
            10-Second Dealer Video Builder
          </h1>
          <p className="text-slate-600">Sora 2 Standard Tier • Generate Real Videos</p>
        </motion.header>

        {/* API Key Section */}
        <motion.div 
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <h3 className="text-lg font-semibold text-slate-800 mb-3">OpenAI API Key</h3>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type={showApiKey ? 'text' : 'password'}
                placeholder="Enter your OpenAI API key (sk-proj-...)"
                value={apiKey}
                onChange={(e) => handleApiKeyChange(e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-slate-700"
              >
                <SafeIcon icon={showApiKey ? FiEyeOff : FiEye} />
              </button>
            </div>
            <div className={`px-3 py-2 rounded-lg text-sm font-medium ${
              isApiKeySet 
                ? 'bg-green-100 text-green-700' 
                : 'bg-amber-100 text-amber-700'
            }`}>
              {isApiKeySet ? '✓ Set' : 'Required'}
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Your API key is stored securely in your browser's local storage and is only used for video generation requests.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            {/* Presets */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Quick Presets</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.keys(presets).map(preset => (
                  <button
                    key={preset}
                    onClick={() => applyPreset(preset)}
                    className="px-3 py-2 text-sm bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>

            {/* Dealership & Offer */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Dealership & Offer</h3>
              <div className="grid gap-4">
                <input
                  type="text"
                  placeholder="Dealership name"
                  value={formData.dealershipName}
                  onChange={(e) => handleInputChange('dealershipName', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                
                <select
                  value={formData.brand}
                  onChange={(e) => handleInputChange('brand', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>

                <input
                  type="text"
                  placeholder="Vehicle model / trim / year"
                  value={formData.vehicleModel}
                  onChange={(e) => handleInputChange('vehicleModel', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />

                <input
                  type="text"
                  placeholder="Offer snippet (e.g., 'Rates from 3.9%')"
                  value={formData.offerSnippet}
                  onChange={(e) => handleInputChange('offerSnippet', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />

                <input
                  type="text"
                  placeholder="CTA (e.g., 'Book a test drive today')"
                  value={formData.cta}
                  onChange={(e) => handleInputChange('cta', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />

                <input
                  type="text"
                  placeholder="Location cue (e.g., 'Halifax waterfront')"
                  value={formData.locationCue}
                  onChange={(e) => handleInputChange('locationCue', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Visual Style & Motion */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Visual Style & Motion</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-1">Tone</label>
                  <select
                    value={formData.tone}
                    onChange={(e) => handleInputChange('tone', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {tones.map(tone => (
                      <option key={tone} value={tone}>{tone}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1">Style</label>
                  <select
                    value={formData.style}
                    onChange={(e) => handleInputChange('style', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {styles.map(style => (
                      <option key={style} value={style}>{style}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1">Camera Move</label>
                  <select
                    value={formData.cameraMove}
                    onChange={(e) => handleInputChange('cameraMove', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {cameraMoves.map(move => (
                      <option key={move} value={move}>{move}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1">Lighting</label>
                  <select
                    value={formData.lighting}
                    onChange={(e) => handleInputChange('lighting', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {lightingOptions.map(light => (
                      <option key={light} value={light}>{light}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-sm text-slate-600 mb-2">Seasonal Theme</label>
                <div className="flex flex-wrap gap-2">
                  {seasonalThemes.map(theme => (
                    <button
                      key={theme}
                      onClick={() => handleInputChange('seasonalTheme', theme)}
                      className={`px-3 py-1 text-sm rounded-full transition-colors ${
                        formData.seasonalTheme === theme
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {theme}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Sound & VO */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Sound & Voiceover</h3>
              <div className="grid gap-4">
                <div>
                  <label className="block text-sm text-slate-600 mb-1">Music Vibe</label>
                  <select
                    value={formData.musicVibe}
                    onChange={(e) => handleInputChange('musicVibe', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {musicVibes.map(vibe => (
                      <option key={vibe} value={vibe}>{vibe}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-slate-600 mb-1">
                    Voiceover Text (optional) - {voiceoverWordCount} words
                    {voiceoverTooLong && (
                      <span className="text-red-500 ml-1">⚠️ Keep under 25 words</span>
                    )}
                  </label>
                  <textarea
                    placeholder="Keep under 20-25 words for 10-second video"
                    value={formData.voiceoverText}
                    onChange={(e) => handleInputChange('voiceoverText', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      voiceoverTooLong ? 'border-red-300' : 'border-slate-300'
                    }`}
                    rows="3"
                  />
                </div>

                {formData.voiceoverText && (
                  <div>
                    <label className="block text-sm text-slate-600 mb-1">Voiceover should end by second</label>
                    <input
                      type="number"
                      min="3"
                      max="10"
                      value={formData.voiceoverEndSecond}
                      onChange={(e) => handleInputChange('voiceoverEndSecond', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Delivery Contact</h3>
              <input
                type="email"
                placeholder="Your email for video delivery (Required)"
                value={formData.contactEmail}
                onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Safety & Rights */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Safety & Rights</h3>
              <div className="space-y-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.safetyConsent}
                    onChange={(e) => handleInputChange('safetyConsent', e.target.checked)}
                    className="mt-1 w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">
                    No real person likenesses without written consent
                  </span>
                </label>

                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={formData.termsAgreement}
                    onChange={(e) => handleInputChange('termsAgreement', e.target.checked)}
                    className="mt-1 w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">
                    I agree not to remove or tamper with watermarks/provenance and to follow platform terms
                  </span>
                </label>
              </div>
            </div>

            {/* Generate Buttons */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  if (voiceoverTooLong) {
                    alert('Voiceover script is a little long. Keep it under 25 words for best timing.');
                    return;
                  }
                  generatePrompt();
                }}
                disabled={!canGeneratePrompt}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiRefreshCw} />
                Generate Prompt
              </button>
              
              {generatedPrompt && (
                <button
                  onClick={() => {
                    if (voiceoverTooLong) {
                      alert('Voiceover script is a little long. Trim it before creating a variant.');
                      return;
                    }
                    generateVariant();
                  }}
                  disabled={!canGeneratePrompt || !generatedPrompt}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  title="Generate A/B variant"
                >
                  <SafeIcon icon={FiShuffle} />
                  A/B
                </button>
              )}
            </div>
          </motion.div>

          {/* Output Section */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            {/* Generated Prompt */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Generated Prompt</h3>
                {generatedPrompt && (
                  <button
                    onClick={() => copyToClipboard(generatedPrompt, 'prompt')}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={copied.prompt ? FiCheckCircle : FiCopy} />
                    {copied.prompt ? 'Copied!' : 'Copy'}
                  </button>
                )}
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4 min-h-[120px]">
                {generatedPrompt ? (
                  <p className="text-slate-700 leading-relaxed">{generatedPrompt}</p>
                ) : (
                  <p className="text-slate-400 italic">Generate a prompt to see it here...</p>
                )}
              </div>
            </div>

            {/* JSON Payload */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Sora 2 API Payload</h3>
                {generatedJSON && (
                  <button
                    onClick={() => copyToClipboard(generatedJSON, 'json')}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={copied.json ? FiCheckCircle : FiCopy} />
                    {copied.json ? 'Copied!' : 'Copy'}
                  </button>
                )}
              </div>
              
              <div className="bg-slate-900 rounded-lg p-4 min-h-[120px]">
                {generatedJSON ? (
                  <pre className="text-green-400 text-sm overflow-x-auto">{generatedJSON}</pre>
                ) : (
                  <p className="text-slate-500 italic">JSON payload will appear here...</p>
                )}
              </div>
            </div>

            {/* Video Generation */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Generate & Deliver Video</h3>
                {generatedVideo && (
                  <button
                    onClick={() => setShowVideoModal(true)}
                    className="flex items-center gap-2 px-3 py-1 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors"
                  >
                    <SafeIcon icon={FiEye} />
                    View Video
                  </button>
                )}
              </div>
              
              <button
                onClick={generateVideo}
                disabled={!canGenerateVideo}
                className="w-full bg-green-600 hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
              >
                {isGeneratingVideo ? (
                  <>
                    <SafeIcon icon={FiLoader} className="animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <SafeIcon icon={FiSend} />
                    Generate & Send via Webhook
                  </>
                )}
              </button>

              {isGeneratingVideo && (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">{videoStatus}</p>
                </div>
              )}

              {webhookStatus && (
                <div className={`mt-4 p-3 rounded-lg ${webhookStatus.includes('failed') ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                  <p className="text-sm font-medium">{webhookStatus}</p>
                </div>
              )}


              {(!hasConsents || missingContactEmail || !generatedPrompt || voiceoverTooLong) && !isGeneratingVideo && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg text-sm text-amber-800 space-y-1">
                  {!hasConsents && <p>Check both safety boxes above so we can keep things compliant.</p>}
                  {voiceoverTooLong && <p>Trim the voiceover under 25 words so the narration fits the 10-second cut.</p>}
                  {!generatedPrompt && hasConsents && !voiceoverTooLong && <p>Generate a prompt first so we know what to build.</p>}
                  {missingContactEmail && <p>Add an email so the finished link reaches your inbox and webhook.</p>}
                </div>
              )}

              {!isApiKeySet && (
                <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium">⚠️ Please set your OpenAI API key above to generate videos.</p>
                </div>
              )}
            </div>

            {/* Footer Notes */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <SafeIcon icon={FiAlertTriangle} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-2">Important Notes:</p>
                  <ul className="space-y-1 text-xs">
                    <li>• Video creation runs directly against the OpenAI Sora 2 API using your key.</li>
                    <li>• Upon completion, the video URL, prompt, and email are sent to your Activepieces webhook.</li>
                    <li>• The webhook URL is hardcoded: <code>{WEBHOOK_URL}</code></li>
                    <li>• Update the preview header constant if OpenAI announces a new beta tag.</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Video Modal */}
      <AnimatePresence>
        {showVideoModal && generatedVideo && generatedVideo.url && (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
                onClick={() => setShowVideoModal(false)}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between p-4 border-b">
                        <h3 className="text-lg font-semibold text-slate-800">Generated Video Preview</h3>
                        <button
                            onClick={() => setShowVideoModal(false)}
                            className="p-1 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                            <SafeIcon icon={FiX} />
                        </button>
                    </div>
                    
                    <div className="p-4">
                        <video
                            controls
                            className="w-full rounded-lg"
                            src={generatedVideo.url}
                            autoPlay
                            loop
                            muted
                        >
                            Your browser does not support the video tag.
                        </video>
                        
                        <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                            <p className="text-sm text-blue-800 font-medium">
                                Video URL sent to: {formData.contactEmail}. Check your Activepieces workflow for delivery status.
                            </p>
                            <a href={generatedVideo.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 underline mt-1 block">
                                Direct Video Link
                            </a>
                        </div>

                        <button
                          onClick={() => downloadVideo(generatedVideo.url, 'dealer-video.mp4')}
                          disabled={isDownloadingVideo}
                          className={`w-full mt-4 py-2 px-4 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${
                            isDownloadingVideo
                              ? 'bg-gray-400 cursor-wait text-white'
                              : 'bg-gray-500 hover:bg-gray-600 text-white'
                          }`}
                        >
                          <SafeIcon icon={isDownloadingVideo ? FiLoader : FiDownload} className={isDownloadingVideo ? 'animate-spin' : ''} />
                          {isDownloadingVideo ? 'Preparing Download...' : 'Download MP4'}
                        </button>

                        {downloadNotice && (
                          <div className="mt-3 p-3 bg-green-50 text-green-700 text-sm rounded-lg">
                            {downloadNotice}
                          </div>
                        )}

                        {downloadError && (
                          <div className="mt-3 p-3 bg-red-50 text-red-700 text-sm rounded-lg">
                            Download failed: {downloadError}
                          </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;